/**
 * RoleFilter — multi-select role picker as a popover-driven chip.
 *
 * - Chip-style trigger button that mirrors the StatusFilter chips next to it.
 * - Popover lists each role as a checkbox row, with an "All Roles" toggle
 *   at the top (partial-fill state when 1–2 are selected).
 * - Draft state inside the popover. "Apply" commits the selection; "Clear"
 *   empties the draft. Closing without Apply discards the draft.
 *
 * Empty selection is treated as "no filter" — show every role. The chip
 * label flips between "All Roles" (empty / all selected) and a "Roles · n"
 * count when partial.
 *
 * Implementation note: native-base's Popover does NOT support render-prop
 * children (function-as-child) with imperative close. Use controlled `isOpen`
 * state instead so we can close the popover from the Apply handler.
 */
import React, { useState } from 'react';
import { Checkbox, ChevronDownIcon, HStack, Popover, Pressable, VStack } from 'native-base';
import { Text } from '@pimm/base';
import type { TeamMemberRole } from '../types';

type Props = {
  // Currently-applied selection. Empty set = "show all roles" (no filter).
  value: Set<TeamMemberRole>;
  onChange: (next: Set<TeamMemberRole>) => void;
  label: string;
  // Per-role labels, in display order (Manager → User → Crew).
  roleLabels: Record<TeamMemberRole, string>;
  // Roles that are actually present in the current view's dataset. The
  // popover only renders checkboxes for these — picking a role that matches
  // zero rows is removed as an option. Computed by the caller per view (Team
  // uses Employee.role; Users uses User.userRole).
  availableRoles: Set<TeamMemberRole>;
  // Localized "All Roles" text shown on the chip when empty / fully selected,
  // and as the top toggle row in the popover.
  allLabel: string;
  applyLabel: string;
  clearLabel: string;
};

// Canonical display order. The popover renders this list intersected with
// `availableRoles` so order is stable even when some roles are dropped.
const ROLE_ORDER: TeamMemberRole[] = ['SMS MANAGER', 'SMS USER', 'SMS EMPLOYEE'];

const CheckRow: React.FC<{
  label: string;
  checked: boolean;
  partial?: boolean;
  onToggle: () => void;
}> = ({ label, checked, partial = false, onToggle }) => (
  <Pressable onPress={onToggle} _hover={{ bg: 'gray.50' }}>
    <HStack px={4} minH={9} alignItems="center" space={2}>
      <Checkbox value={label} isChecked={checked} isIndeterminate={partial} isReadOnly accessibilityLabel={label} />
      <Text size="md" fontWeight={600} color="gray.800" flex={1} numberOfLines={1}>
        {label}
      </Text>
    </HStack>
  </Pressable>
);

export const RoleFilter: React.FC<Props> = ({ value, onChange, label, roleLabels, availableRoles, allLabel, applyLabel, clearLabel }) => {
  // Render only roles that exist in the current view's dataset, in canonical
  // display order. Roles not in `availableRoles` are dropped.
  const visibleRoles = ROLE_ORDER.filter(r => availableRoles.has(r));
  const totalCount = visibleRoles.length;
  const selectedCount = value.size;
  const isAll = selectedCount === 0 || selectedCount === totalCount;
  const chipLabel = isAll ? allLabel : `${label} · ${selectedCount}`;
  const chipActive = !isAll;

  // Controlled open state lets us close the popover from the Apply / Clear
  // handlers; uncontrolled native-base Popover doesn't expose a close fn.
  const [isOpen, setIsOpen] = useState(false);
  // Draft committed on Apply; reset to current `value` each time the popover
  // opens so re-entering after Cancel snaps back to applied state.
  const [draft, setDraft] = useState<Set<TeamMemberRole>>(() => new Set(value));

  const handleOpen = () => {
    setDraft(new Set(value));
    setIsOpen(true);
  };
  const handleClose = () => setIsOpen(false);

  const draftSize = draft.size;
  const allChecked = totalCount > 0 && draftSize === totalCount;
  const partial = draftSize > 0 && !allChecked;

  const toggleRole = (role: TeamMemberRole) => {
    setDraft(prev => {
      const next = new Set(prev);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return next;
    });
  };

  const toggleAll = () => {
    setDraft(prev => (prev.size === totalCount ? new Set() : new Set(visibleRoles)));
  };

  const handleClear = () => setDraft(new Set());
  const handleApply = () => {
    onChange(new Set(draft));
    setIsOpen(false);
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      trigger={triggerProps => (
        <Pressable {...triggerProps}>
          <HStack
            px={3}
            h={9}
            space={2}
            alignItems="center"
            justifyContent="center"
            rounded="lg"
            shadow={1}
            bg={chipActive ? 'primary.50' : 'white'}
            borderWidth={1}
            borderColor={chipActive ? 'primary.600' : 'gray.300'}
          >
            <Text size="sm" fontWeight={chipActive ? 600 : 500} color={chipActive ? 'primary.700' : 'gray.700'}>
              {chipLabel}
            </Text>
            <ChevronDownIcon size={3} color={chipActive ? 'primary.700' : 'gray.500'} />
          </HStack>
        </Pressable>
      )}
      placement="bottom left"
    >
      <Popover.Content w={240} borderWidth={0} shadow="7">
        <Popover.Body p={0} bg="white">
          <VStack py={1}>
            <CheckRow label={allLabel} checked={allChecked} partial={partial} onToggle={toggleAll} />
            {visibleRoles.map(role => (
              <CheckRow key={role} label={roleLabels[role]} checked={draft.has(role)} onToggle={() => toggleRole(role)} />
            ))}
          </VStack>
          <HStack justifyContent="space-between" alignItems="center" px={4} py={2} borderTopWidth={1} borderTopColor="gray.100">
            <Pressable onPress={handleClear} _hover={{ opacity: 0.7 }}>
              <Text size="md" fontWeight={600} color="gray.600" underline>
                {clearLabel}
              </Text>
            </Pressable>
            <Pressable onPress={handleApply} bg="primary.500" px={4} py={1.5} rounded="md" _hover={{ bg: 'primary.600' }}>
              <Text size="md" fontWeight={600} color="white">
                {applyLabel}
              </Text>
            </Pressable>
          </HStack>
        </Popover.Body>
      </Popover.Content>
    </Popover>
  );
};
